import React, { useEffect } from 'react';
import { Grid, Typography } from '@material-ui/core';
import { Delete } from '@material-ui/icons';
import { IconButton, Switch, TextField } from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';
import { getOptions } from '@michelin/select-options-provider';
import Autocomplete from 'components/Autocomplete';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import uuid from 'uuid';
import { BillingProfileContextData, BillingWheelOffInstalled, BillingWheelRequestedDetails } from '../types';

interface WheelItemProps {
  index: number;
  editFlag: boolean;
  bothAssigned: boolean;
  onRemove: (index: number) => void;
  key: string;
}

export function WheelItem({ key, index, editFlag, bothAssigned, onRemove }: WheelItemProps) {
  const { t } = useTranslation();
  const { setValue, control, watch } = useFormContext<BillingProfileContextData>();

  const wheelDetail = watch(`profile.wheel_details.${index}`);
  return (
    <React.Fragment key={key}>
      <Grid item xs={12} md={2} style={{ display: 'flex', alignItems: 'flex-end' }}>
        <Controller
          name={`profile.wheel_details.${index}.off_installed`}
          control={control}
          render={(item) => (
            <Switch
              checked={item.field.value === BillingWheelOffInstalled.INSTALLED_WHEELS}
              disabled={!editFlag || bothAssigned}
              label=""
              checkedLabel={t('Installed')}
              uncheckedLabel={t('Off')}
              labelPosition="top"
              inputProps={{
                'aria-label': 'installed-switch',
              }}
              onChange={(e) => {
                const newValue = e.target.checked
                  ? BillingWheelOffInstalled.INSTALLED_WHEELS
                  : BillingWheelOffInstalled.OFF_WHEELS;
                item.field.onChange(newValue);
              }}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={5} style={{ display: 'flex', alignItems: 'flex-end' }}>
        <div style={{ width: '100%' }}>
          <Controller
            name={`profile.wheel_details.${index}.requested_details`}
            control={control}
            rules={{
              required: { value: true, message: t('Requested Wheel Details are required.') },
            }}
            render={(item) => (
              <Autocomplete
                id="requested-details-autocomplete"
                data-testid="requested-details-test"
                multiple
                variant="standard"
                label={t('Requested Details')}
                disableClearable
                disableCloseOnSelect
                placeholder=""
                onChange={(value) => {
                  if (!value.includes(BillingWheelRequestedDetails.OTHER)) {
                    setValue(`profile.wheel_details.${index}.other`, '');
                  }
                  item.field.onChange(value);
                }}
                suggestions={getOptions('wheel_requested_details')}
                values={item.field.value}
                error={!!item.formState?.errors?.profile?.wheel_details?.[index]?.requested_details}
                readOnly={!editFlag}
              />
            )}
          />
        </div>
      </Grid>
      <Grid item xs={12} md={4} style={{ display: 'flex', alignItems: 'flex-end' }}>
        <Controller
          name={`profile.wheel_details.${index}.other`}
          control={control}
          render={(item) => (
            <TextField
              value={item.field.value}
              label={t('Other')}
              inputProps={{
                'aria-label': 'other-textfield',
              }}
              readOnly={!editFlag || !wheelDetail.requested_details?.includes(BillingWheelRequestedDetails.OTHER)}
              onChange={(e) => {
                item.field.onChange(e.target.value);
              }}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={1} style={{ display: 'flex', alignItems: 'center' }}>
        <IconButton
          color="primary"
          size="small"
          variant="default"
          disabled={!editFlag}
          onClick={() => onRemove(index)}
          aria-label="remove"
        >
          <Delete fontSize="small" />
        </IconButton>
      </Grid>
    </React.Fragment>
  );
}

interface WheelListProps {
  editFlag: boolean;
  bothAssigned: boolean;
  clickedAdd: boolean;
  setClickedAdd: (clickedAdd: boolean) => void;
}

export function WheelList({ editFlag, bothAssigned, clickedAdd, setClickedAdd }: WheelListProps) {
  const { t } = useTranslation();
  useEffect(() => {
    if (clickedAdd) add();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clickedAdd]);
  const { setValue, watch, control } = useFormContext<BillingProfileContextData>();

  const { remove } = useFieldArray({
    control,
    name: 'profile.wheel_details',
  });

  const list = watch('profile.wheel_details');

  const add = () => {
    setClickedAdd(false);
    if (!list || list.length === 0) {
      const wheel = {
        id: uuid.v4(),
        requested_details: [],
        off_installed: BillingWheelOffInstalled.OFF_WHEELS,
        other: '',
      };
      setValue('profile.wheel_details', [wheel], { shouldDirty: true });
      return;
    }
    if (!list[list.length - 1].requested_details || list[list.length - 1]?.requested_details?.length === 0) return;
    if (list) {
      const lastWheelDetails = list[list.length - 1];
      if (lastWheelDetails && lastWheelDetails.requested_details && lastWheelDetails.requested_details.length > 0) {
        if (
          lastWheelDetails.off_installed === BillingWheelOffInstalled.OFF_WHEELS ||
          lastWheelDetails.off_installed === undefined
        ) {
          const wheel = {
            id: uuid.v4(),
            requested_details: [],
            off_installed: BillingWheelOffInstalled.INSTALLED_WHEELS,
          };
          setValue('profile.wheel_details', [...list, wheel], { shouldDirty: true });
        } else {
          const wheel = {
            id: uuid.v4(),
            requested_details: [],
            off_installed: BillingWheelOffInstalled.OFF_WHEELS,
          };
          setValue('profile.wheel_details', [...list, wheel], { shouldDirty: true });
        }
        return;
      }
    }
  };

  return (
    <>
      {!list || list.length === 0 ? (
        <Typography style={{ color: '#999999', width: '100%', textAlign: 'center' }}>{t('No data...')}</Typography>
      ) : (
        <>
          {list.map((row, index) => (
            <WheelItem
              key={`wheelRow_${row.off_installed}`}
              index={index}
              editFlag={editFlag}
              bothAssigned={bothAssigned}
              onRemove={remove}
            />
          ))}
        </>
      )}
    </>
  );
}
